// Hooks da fatia S2 (conexões com Google/Meta/demonstração).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AdProvider } from '@impulso/shared'
import type {
  ConnectionLinkCreated, ConnectionLinkInfo, ConnectionsListResponse, PendingSelectionDto, ProvidersResponse, SelectAccountResponse,
  StartConnectionResponse, SyncRequestResponse,
} from '@impulso/shared/api/connections'
import { api } from '../../lib/api'

export const connectionsKey = (companyId: string) => ['connections', companyId] as const

export function useProviders() {
  return useQuery({ queryKey: ['connectors', 'providers'], queryFn: () => api.get<ProvidersResponse>('/connectors/providers'), staleTime: 300_000 })
}

/** Lista de conexões; atualiza sozinha enquanto há importação/sincronização em andamento. */
export function useConnections(companyId: string) {
  return useQuery({
    queryKey: connectionsKey(companyId),
    queryFn: () => api.get<ConnectionsListResponse>(`/companies/${companyId}/connections`),
    refetchInterval: (q) => {
      const items = q.state.data?.items ?? []
      return items.some((c) => c.importing || c.syncStates.some((s) => s.running)) ? 4000 : false
    },
  })
}

/** Inicia o OAuth e redireciona o navegador para a página oficial da plataforma. */
export function useStartConnection(companyId: string) {
  return useMutation({
    mutationFn: (input: AdProvider | { provider: AdProvider; returnTo?: string | null }) =>
      api.post<StartConnectionResponse>(`/companies/${companyId}/connections/start`, typeof input === 'string' ? { provider: input } : input),
    onSuccess: ({ authUrl }) => window.location.assign(authUrl),
  })
}

export function usePendingSelection(companyId: string, pendingId: string | null) {
  return useQuery({
    queryKey: ['connections', companyId, 'pending', pendingId],
    queryFn: () => api.get<PendingSelectionDto>(`/companies/${companyId}/connections/pending/${pendingId}`),
    enabled: !!pendingId,
    retry: false,
  })
}

export function useSelectAccount(companyId: string, pendingId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (externalAccountId: string) => api.post<SelectAccountResponse>(`/companies/${companyId}/connections/pending/${pendingId}/select`, { externalAccountId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: connectionsKey(companyId) }),
  })
}

export function useSyncConnection(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (connectionId: string) => api.post<SyncRequestResponse>(`/companies/${companyId}/connections/${connectionId}/sync`),
    onSuccess: () => setTimeout(() => void qc.invalidateQueries({ queryKey: connectionsKey(companyId) }), 1500),
  })
}

export function useDisconnect(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (connectionId: string) => api.del<{ ok: true }>(`/companies/${companyId}/connections/${connectionId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: connectionsKey(companyId) }),
  })
}

export function useCreateConnectionLink(companyId: string) {
  return useMutation({
    mutationFn: (provider: AdProvider | null) => api.post<ConnectionLinkCreated>(`/companies/${companyId}/connection-links`, { provider }),
  })
}

// ---------------------------------------------------------------- fluxo público (link)

export function useLinkInfo(token: string) {
  return useQuery({ queryKey: ['connection-link', token], queryFn: () => api.get<ConnectionLinkInfo>(`/connection-links/${token}`), retry: false })
}

export function useLinkStart(token: string) {
  return useMutation({
    mutationFn: (provider: AdProvider) => api.post<StartConnectionResponse>(`/connection-links/${token}/start`, { provider }),
    onSuccess: ({ authUrl }) => window.location.assign(authUrl),
  })
}

export function useLinkPending(token: string, pendingId: string | null) {
  return useQuery({
    queryKey: ['connection-link', token, 'pending', pendingId],
    queryFn: () => api.get<PendingSelectionDto>(`/connection-links/${token}/pending/${pendingId}`),
    enabled: !!pendingId,
    retry: false,
  })
}

export function useLinkSelect(token: string, pendingId: string | null) {
  return useMutation({
    mutationFn: (externalAccountId: string) => api.post<{ ok: true }>(`/connection-links/${token}/pending/${pendingId}/select`, { externalAccountId }),
  })
}
